import type { ReactNode } from 'react'
import { AtSignIcon, Link01Icon, LinkBroken02Icon } from '@systemeio/icons'
import { useCurrentEditor, useEditorState } from '@tiptap/react'
import { cn } from 'cn'
import type { Emoji } from 'emoji-mart'
import EmbeddedVideoAttachment from 'modules/attachments/components/embedded-video-attachment'
import { GifAttachment } from 'modules/attachments/components/gif-attachment'
import { UploadAttachment } from 'modules/attachments/components/upload-attachment'
import { ToolbarButton } from './common/toolbar-button'
import { EmojiPicker } from './elements/emoji-picker/emoji-picker'
import { LinkModal } from './elements/Link/link-modal'

export const TiptapToolbar = ({
  className,
  path,
}: {
  className?: string
  path?: string
}): ReactNode => {
  const { editor } = useCurrentEditor()
  const linkActive =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) => currentEditor?.isActive('link') ?? false,
    }) ?? false

  if (!editor) return null

  const handleInsertLink = (url: string): void => {
    if (editor.state.selection.empty) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }],
        })
        .insertContent(' ')
        .run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    editor.commands.setTextSelection(editor.state.selection.to)
  }

  return (
    <div
      data-group-control
      className={cn('flex items-center gap-2 md:gap-3', className)}
      onClick={event => {
        event.stopPropagation()
      }}
    >
      <UploadAttachment
        className="group"
        iconClassName="size-5 text-text-secondary group-hover:text-text-primary md:size-6"
      />
      <EmbeddedVideoAttachment iconClassName="size-5 md:size-6" />
      <GifAttachment iconClassName="size-5 text-text-secondary hover:text-text-primary md:size-6" />
      {linkActive ? (
        <ToolbarButton
          format="unlink"
          active
          onClick={() => {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
          }}
        >
          <LinkBroken02Icon className="size-5 text-[initial] md:size-6" />
        </ToolbarButton>
      ) : (
        <LinkModal
          trigger={
            <ToolbarButton format="link">
              <Link01Icon className="size-5 text-text-secondary md:size-6" />
            </ToolbarButton>
          }
          onInsertLink={handleInsertLink}
        />
      )}
      {path ? (
        <ToolbarButton
          format="mention"
          onClick={() => {
            editor.chain().focus().insertContent('@').run()
          }}
        >
          <AtSignIcon className="size-5 text-text-secondary hover:text-[initial] md:size-6" />
        </ToolbarButton>
      ) : null}
      <EmojiPicker
        iconClassName="size-5 md:size-6"
        onEmojiSelect={(emoji: typeof Emoji.Props) => {
          editor.chain().focus().insertContent(emoji.native).run()
        }}
      />
    </div>
  )
}
